import { queryOptions, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getJson, postJson, postForm, patchJson, putJson, deleteJson, isHttpError } from './api';
import { getCurrentUser } from './auth';
import type {
  PatientSummary,
  PatientDetail,
  PatientWithFields,
  DashboardOverviewResponse,
  JobSummary,
  ImageSummary,
  AuditLogEntry,
  ExtractedFields,
  EyeSummaryResponse,
  PatchEyeFieldsRequest,
  PatientFields,
  PatientImageSummary,
  WorkerStatus,
  SettingsStatus,
  PaginatedPatientsResponse,
  StageTypeSummary,
  TimelineGroup,
  PiiConfig,
  PiiDashboard,
  PiiImageSummary,
  PiiFinding,
  PiiResolveRequest,
  PiiResolveResponse,
  PiiStatus,
  PatientConflictSummary,
  ActiveSession,
  UpcomingWindow,
  TrendWindow,
  UpcomingLoadResponse,
  LoadTrendResponse,
  DashboardAlertsResponse,
} from './types';
import type {
  SurgicalListSummary,
  SurgicalListDetail,
  CreateSurgicalListRequest,
  UpdateSurgicalListRequest,
  CreateSurgicalListItemRequest,
  UpdateSurgicalListItemRequest,
  ValidationResponse,
  ListStatus,
  ListType,
  EyeSide,
  SurgeryStage,
  AppointmentSubtype,
  SchedulingConfig,
  ScheduleReturnsRequest,
  ScheduleReturnsResponse,
  PatientAppointment,
} from '../types/surgical-list';
import type { Member, MemberLevel, MemberFilter } from '../types/member';
import type { DocumentType } from '../types/document-type';
import type { AbsenceType, AbsenceTypeFormValues } from '../types/absence-type';
import type {
  FieldDefinition,
  ExamTemplateSummary,
  ExamTemplateDetail,
  ExamSessionSummary,
  ExamSessionDetail,
  ClinicalValueEntry,
  TimelineEntry,
  CreateExamSessionRequest,
} from '../types/clinical-exam';
import type {
  MemberGroupSummary,
  MemberGroupMember,
  ActivityTypeSummary,
  RotationSlotSummary,
  ProjectedDay,
  AvailableSlot,
  RotationOverrideSummary,
  FollowUpProtocolSummary,
  EvolutionNoteSummary,
  FinalizeResponse,
} from '../types/rotation';
import type { RotationOverrideType } from '../types/rotation-override';
import type {
  ScenarioSummary,
  ScenarioDetail,
  PracticeSessionSummary,
  PracticeSessionDetail,
  SessionScoreDetail,
  PerformanceAggregate,
  PaginatedResponse,
  CreatePracticeSessionRequest,
  SubmitDecisionRequest,
  StartSessionRequest,
  CompleteSessionRequest,
  AbandonSessionRequest,
  DecisionSubmitResult,
  SessionCreateResult,
  SessionStartResult,
  SessionCompleteResult,
  SessionAbandonResult,
  ScenarioCalibration,
  TopPerformersResult,
} from '../types/practice';

// ============================================================================
// Query Key Factory - Centralized key management for consistency
// ============================================================================
export const queryKeys = {
  all: () => ['dominoris', getCurrentUser()?.orgId ?? 0] as const,

  // Patients
  patients: () => [...queryKeys.all(), 'patients'] as const,
  patientsList: (status?: string) => [...queryKeys.patients(), 'list', { status }] as const,
  patientsScheduled: (date?: string) => [...queryKeys.patients(), 'scheduled', { date }] as const,
  patientDetail: (id: number) => [...queryKeys.patients(), 'detail', id] as const,
  patientEyeSummary: (id: number) => [...queryKeys.patients(), 'eye-summary', id] as const,
  patientSearch: (q: string) => [...queryKeys.patients(), 'search', q] as const,
  patientsPaginated: (params: { page: number; limit: number; status?: string; search?: string; listId?: string }) =>
    [...queryKeys.patients(), 'paginated', params] as const,

  // Dashboard
  dashboard: () => [...queryKeys.all(), 'dashboard'] as const,
  dashboardUpcoming: (windowDays: number) => [...queryKeys.all(), 'dashboard', 'upcoming', windowDays] as const,
  dashboardTrend: (windowDays: number) => [...queryKeys.all(), 'dashboard', 'trend', windowDays] as const,
  dashboardAlerts: () => [...queryKeys.all(), 'dashboard', 'alerts'] as const,

  // Jobs
  jobs: () => [...queryKeys.all(), 'jobs'] as const,
  jobsList: (status?: string) => [...queryKeys.jobs(), 'list', { status }] as const,

  // Images
  images: () => [...queryKeys.all(), 'images'] as const,
  imagesList: (params?: { linked?: boolean; patientId?: string }) =>
    [...queryKeys.images(), 'list', params] as const,
  imagesOrphanStats: () => [...queryKeys.images(), 'orphanStats'] as const,

  // Audit
  auditLogs: () => [...queryKeys.all(), 'auditLogs'] as const,
  auditLogsList: (page: number, limit: number) => [...queryKeys.auditLogs(), { page, limit }] as const,

  // Worker
  worker: () => [...queryKeys.all(), 'worker'] as const,
  workerStatus: () => [...queryKeys.worker(), 'status'] as const,

  // Settings
  settings: () => [...queryKeys.all(), 'settings'] as const,

  // User Preferences
  userPreferences: () => [...queryKeys.all(), 'userPreferences'] as const,
  userOauthLinks: () => [...queryKeys.all(), 'userOauthLinks'] as const,

  // Members
  members: () => [...queryKeys.all(), 'members'] as const,
  memberSearch: (q: string, filter?: MemberFilter) => [...queryKeys.members(), 'search', { q, ...filter }] as const,
  membersList: (filter?: MemberFilter) => [...queryKeys.members(), 'list', { ...filter }] as const,
  levels: () => [...queryKeys.all(), 'levels'] as const,

  // Logs
  logs: () => [...queryKeys.all(), 'logs'] as const,
  logStats: () => [...queryKeys.logs(), 'stats'] as const,
  logSources: () => [...queryKeys.logs(), 'sources'] as const,

  // Surgical Lists
  surgicalLists: () => [...queryKeys.all(), 'surgicalLists'] as const,
  surgicalListsList: (filters?: { dateFrom?: string; dateTo?: string; status?: string }) =>
    [...queryKeys.surgicalLists(), 'list', filters] as const,
  surgicalListDetail: (id: string) => [...queryKeys.surgicalLists(), 'detail', id] as const,

  // Document Types
  documentTypes: () => [...queryKeys.all(), 'documentTypes'] as const,

  // Absence Types (EXK-344)
  absenceTypes: () => [...queryKeys.all(), 'absenceTypes'] as const,

  // Appointment Subtypes
  appointmentSubtypes: () => [...queryKeys.all(), 'appointmentSubtypes'] as const,

  // Scheduling Config
  schedulingConfig: () => [...queryKeys.all(), 'schedulingConfig'] as const,

  // Patient Appointments
  patientAppointments: (id: number) => [...queryKeys.patients(), 'appointments', id] as const,
  patientTimeline: (id: number) => [...queryKeys.patients(), 'timeline', id] as const,

  // Panoramic Calendar
  panoramic: (dateFrom?: string, dateTo?: string) =>
    [...queryKeys.all(), 'panoramic', { dateFrom, dateTo }] as const,

  // Member Groups
  memberGroups: () => [...queryKeys.all(), 'member-groups'] as const,
  memberGroupMembers: (groupId: number) => [...queryKeys.all(), 'member-groups', groupId, 'members'] as const,

  // Activity Types
  activityTypes: () => [...queryKeys.all(), 'activity-types'] as const,

  // Rotation
  rotationTemplate: () => [...queryKeys.all(), 'rotation', 'template'] as const,
  rotationCalendar: (from: string, to: string) => [...queryKeys.all(), 'rotation', 'calendar', from, to] as const,
  rotationAvailability: (from: string, to: string, activityType?: string) =>
    [...queryKeys.all(), 'rotation', 'availability', from, to, activityType] as const,
  rotationOverrides: (from?: string, to?: string) => [...queryKeys.all(), 'rotation', 'overrides', from, to] as const,

  // Follow-Up Protocols
  followUpProtocols: () => [...queryKeys.all(), 'follow-up-protocols'] as const,

  // Evolution Notes
  evolutionNotes: (itemId: number) => [...queryKeys.all(), 'evolution-notes', itemId] as const,
  patientEvolutions: (patientId: number, from?: string, to?: string) => [...queryKeys.all(), 'patient-evolutions', patientId, { from, to }] as const,

  // Stage Types
  stageTypes: () => [...queryKeys.all(), 'stage-types'] as const,

  // PII
  piiConfig: () => [...queryKeys.all(), 'pii-config'] as const,
  piiDashboard: () => [...queryKeys.all(), 'pii-dashboard'] as const,
  piiImages: (status: string) => [...queryKeys.all(), 'pii-images', status] as const,
  piiFindings: (imageId: number) => [...queryKeys.all(), 'pii-findings', imageId] as const,

  // Practice - Scenarios
  scenarios: () => [...queryKeys.all(), 'scenarios'] as const,
  scenariosList: (filters?: { specialty?: string; difficulty?: string }) =>
    [...queryKeys.scenarios(), 'list', filters] as const,
  scenarioDetail: (id: number) => [...queryKeys.scenarios(), 'detail', id] as const,

  // Practice - Sessions
  practiceSessions: () => [...queryKeys.all(), 'practice-sessions'] as const,
  practiceSessionsList: (filters?: { status?: string }) =>
    [...queryKeys.practiceSessions(), 'list', filters] as const,
  practiceSession: (id: number) => [...queryKeys.practiceSessions(), 'detail', id] as const,
  practiceScore: (sessionId: number) => [...queryKeys.practiceSessions(), 'score', sessionId] as const,
  scenarioCalibration: (scenarioId: number) =>
    [...queryKeys.practiceSessions(), 'calibration', scenarioId] as const,
  scenarioTopPerformers: (scenarioId: number, limit: number) =>
    [...queryKeys.practiceSessions(), 'top', scenarioId, { limit }] as const,

  // Practice - Performance (F23)
  practicePerformance: () => [...queryKeys.all(), 'practice-performance'] as const,

  // Clinical exams
  fieldDefinitions: () => [...queryKeys.all(), 'field-definitions'] as const,
  examTemplates: () => [...queryKeys.all(), 'exam-templates'] as const,
  examTemplateDetail: (id: number) => [...queryKeys.examTemplates(), 'detail', id] as const,
  examSessions: (filters?: Record<string, unknown>) => [...queryKeys.all(), 'exam-sessions', filters] as const,
  examSessionDetail: (id: number) => [...queryKeys.all(), 'exam-sessions', 'detail', id] as const,
  clinicalValues: (patientId: number, filters?: Record<string, unknown>) => [...queryKeys.all(), 'clinical-values', patientId, filters] as const,
  timeline: (patientId: number) => [...queryKeys.all(), 'timeline', patientId] as const,

  // Auth — active sessions (per-user, MAX_SESSIONS UI)
  activeSessions: () => [...queryKeys.all(), 'active-sessions'] as const,
};

// ============================================================================
// Query Options - Type-safe, reusable query configurations
// ============================================================================

// Patients
// Polling cadence: 5s when any patient has OCR jobs in flight (server signals
// via hasProcessingImages), otherwise 15s as a safety fallback for OCR
// completions that bypassed the optimistic invalidation path.
export const patientsQueryOptions = (status?: string) =>
  queryOptions({
    queryKey: queryKeys.patientsList(status),
    queryFn: () => {
      const url = status ? `/patients?status=${status}` : '/patients';
      return getJson<PatientSummary[]>(url);
    },
    staleTime: 1000 * 60 * 2, // 2 minutes
    refetchInterval: (query) => {
      const data = query.state.data;
      if (Array.isArray(data) && data.some((p) => p.hasProcessingImages)) return 5_000;
      return 15_000;
    },
  });

export const scheduledPatientsQueryOptions = (date?: string) =>
  queryOptions({
    queryKey: queryKeys.patientsScheduled(date),
    queryFn: () => {
      const url = date
        ? `/patients/scheduled/with-fields?date=${date}`
        : '/patients/scheduled/with-fields';
      return getJson<PatientWithFields[]>(url);
    },
    staleTime: 1000 * 60 * 2,
  });

export const paginatedPatientsQueryOptions = (params: {
  page: number;
  limit: number;
  status?: string;
  search?: string;
  listId?: string;
}) =>
  queryOptions({
    queryKey: queryKeys.patientsPaginated(params),
    queryFn: () => {
      const searchParams = new URLSearchParams();
      searchParams.set('page', String(params.page));
      searchParams.set('limit', String(params.limit));
      if (params.status) searchParams.set('status', params.status);
      if (params.search) searchParams.set('search', params.search);
      if (params.listId) searchParams.set('listId', params.listId);
      return getJson<PaginatedPatientsResponse>(`/patients/paginated?${searchParams}`);
    },
    staleTime: 1000 * 60 * 2,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data && Array.isArray(data.data) && data.data.some((p) => p.hasProcessingImages)) {
        return 5_000;
      }
      return 30_000;
    },
    // Pausa polling TanStack quando a tab esta oculta (foreground only). Nao afeta
    // Service Worker — Workbox so cacheia assets staticos, nao tem bg-sync de /api.
    refetchIntervalInBackground: false,
    // Override do QueryClient global (main.tsx:31 refetchOnWindowFocus:false). Pra
    // lista de pacientes, queremos refresh imediato quando o user volta da camera
    // ou de outra tab — caso classico: subiu foto, voltou pra ver OCR finalizar.
    // 'always' bypass staleTime de 2min: refresh REAL imediato (Codex audit pos-
    // deploy: simples 'true' respeitava staleTime e nao refresh em <2min).
    refetchOnWindowFocus: 'always',
  });

export const patientDetailQueryOptions = (id: number) =>
  queryOptions({
    queryKey: queryKeys.patientDetail(id),
    queryFn: () => getJson<PatientDetail>(`/patients/${id}`),
    enabled: id > 0,
    staleTime: 1000 * 60 * 2,
  });

export const patientEyeSummaryQueryOptions = (id: number) =>
  queryOptions({
    queryKey: queryKeys.patientEyeSummary(id),
    queryFn: () => getJson<EyeSummaryResponse>(`/patients/${id}/eye-summary`),
    enabled: id > 0,
    staleTime: 1000 * 60 * 2,
  });

// Dashboard
export const dashboardQueryOptions = () =>
  queryOptions({
    queryKey: queryKeys.dashboard(),
    queryFn: () => getJson<DashboardOverviewResponse>('/dashboard/overview'),
    staleTime: 1000 * 30, // 30 seconds for dashboard
  });

// Dashboard — split endpoints (parallel queries with independent skeletons)
export const upcomingLoadQueryOptions = (windowDays: UpcomingWindow) =>
  queryOptions({
    queryKey: queryKeys.dashboardUpcoming(windowDays),
    queryFn: () => getJson<UpcomingLoadResponse>(`/dashboard/upcoming-load?windowDays=${windowDays}`),
    staleTime: 1000 * 60, // 60s — surgical lists change moderately
  });

export const loadTrendQueryOptions = (windowDays: TrendWindow) =>
  queryOptions({
    queryKey: queryKeys.dashboardTrend(windowDays),
    queryFn: () => getJson<LoadTrendResponse>(`/dashboard/load-trend?windowDays=${windowDays}`),
    staleTime: 1000 * 60 * 5, // 5min — historical, changes slowly
  });

export const dashboardAlertsQueryOptions = () =>
  queryOptions({
    queryKey: queryKeys.dashboardAlerts(),
    queryFn: () => getJson<DashboardAlertsResponse>('/dashboard/alerts'),
    staleTime: 1000 * 30, // 30s — alerts can pop in fast
  });

// Conflict patients
export const conflictPatientsQueryOptions = () =>
  queryOptions({
    queryKey: [...queryKeys.patients(), 'with-conflicts'] as const,
    queryFn: () => getJson<PatientConflictSummary[]>('/patients/with-conflicts'),
    staleTime: 1000 * 60, // 1 minute
  });

// Upcoming surgical lists
export const surgicalListsUpcomingQueryOptions = (dateFrom: string) =>
  queryOptions({
    queryKey: [...queryKeys.surgicalLists(), 'upcoming', dateFrom] as const,
    queryFn: () => getJson<SurgicalListSummary[]>(`/surgical-lists?dateFrom=${dateFrom}`),
    staleTime: 1000 * 60, // 1 minute
  });

// Jobs
export const jobsQueryOptions = (status?: string) =>
  queryOptions({
    queryKey: queryKeys.jobsList(status),
    queryFn: () => {
      const url = status ? `/jobs?status=${status}` : '/jobs';
      return getJson<JobSummary[]>(url);
    },
    staleTime: 1000 * 10, // 10 seconds - jobs change frequently
    refetchInterval: 1000 * 10,
  });

// Images
export const imagesQueryOptions = (params?: { linked?: boolean; patientId?: string }) =>
  queryOptions({
    queryKey: queryKeys.imagesList(params),
    queryFn: () => {
      const searchParams = new URLSearchParams();
      if (params?.linked !== undefined) searchParams.set('linked', String(params.linked));
      if (params?.patientId) searchParams.set('patientId', params.patientId);
      const url = `/images${searchParams.toString() ? `?${searchParams}` : ''}`;
      return getJson<ImageSummary[]>(url);
    },
    staleTime: 1000 * 60 * 2,
  });

export const orphanStatsQueryOptions = () =>
  queryOptions({
    queryKey: queryKeys.imagesOrphanStats(),
    queryFn: () => getJson<{ total: number; unlinked: number }>('/images/orphan-stats'),
    staleTime: 1000 * 60 * 2,
  });

// Audit Logs
// API returns { data: AuditLogEntry[], total, page, limit }
export const auditLogsQueryOptions = (page = 1, limit = 50) =>
  queryOptions({
    queryKey: queryKeys.auditLogsList(page, limit),
    queryFn: () =>
      getJson<{ data: AuditLogEntry[]; total: number; page: number; limit: number }>(
        `/audit-logs?page=${page}&limit=${limit}`,
      ).then((res) => ({ logs: res.data, total: res.total })),
    staleTime: 1000 * 60 * 5,
  });

// Worker Status
export const workerStatusQueryOptions = () =>
  queryOptions({
    queryKey: queryKeys.workerStatus(),
    queryFn: () => getJson<WorkerStatus[]>('/worker/status'),
    staleTime: 1000 * 30, // 30 seconds
    refetchInterval: 1000 * 30,
  });

// Settings
export const settingsStatusQueryOptions = () =>
  queryOptions({
    queryKey: queryKeys.settings(),
    queryFn: () => getJson<SettingsStatus>('/settings/status'),
    staleTime: 1000 * 60 * 5,
  });

// User OAuth Links (e.g. Google linked state)
export const userOauthLinksQueryOptions = () =>
  queryOptions({
    queryKey: queryKeys.userOauthLinks(),
    queryFn: () =>
      getJson<{ googleLinked: boolean; googleUnlinkedAt: string | null }>(
        '/users/me/oauth-links',
      ),
    staleTime: 1000 * 60 * 5,
  });

// User Preferences
export const userPreferencesQueryOptions = () =>
  queryOptions({
    queryKey: queryKeys.userPreferences(),
    queryFn: () => getJson<Record<string, unknown>>('/users/me/preferences'),
    staleTime: 1000 * 60 * 10,
  });

// ============================================================================
// Query Hooks - Convenient wrappers for components
// ============================================================================

export const usePatients = (status?: string) => useQuery(patientsQueryOptions(status));
export const useScheduledPatients = (date?: string) => useQuery(scheduledPatientsQueryOptions(date));
export const usePaginatedPatients = (params: {
  page: number;
  limit: number;
  status?: string;
  search?: string;
  listId?: string;
  enabled?: boolean;
}) => {
  const { enabled = true, ...queryParams } = params;
  return useQuery({ ...paginatedPatientsQueryOptions(queryParams), enabled });
};
export const usePatientDetail = (id: number) => useQuery(patientDetailQueryOptions(id));
export const usePatientEyeSummary = (id: number) => useQuery(patientEyeSummaryQueryOptions(id));
export const useDashboard = () => useQuery(dashboardQueryOptions());
export const useConflictPatients = () => useQuery(conflictPatientsQueryOptions());
export const useSurgicalListsUpcoming = (dateFrom: string) => useQuery(surgicalListsUpcomingQueryOptions(dateFrom));
export const useUpcomingLoad = (windowDays: UpcomingWindow) => useQuery(upcomingLoadQueryOptions(windowDays));
export const useLoadTrend = (windowDays: TrendWindow) => useQuery(loadTrendQueryOptions(windowDays));
export const useDashboardAlerts = () => useQuery(dashboardAlertsQueryOptions());
export const useJobs = (status?: string) => useQuery(jobsQueryOptions(status));
export const useImages = (params?: { linked?: boolean; patientId?: string }) =>
  useQuery(imagesQueryOptions(params));
export const useOrphanStats = () => useQuery(orphanStatsQueryOptions());
export const useAuditLogs = (page?: number, limit?: number) =>
  useQuery(auditLogsQueryOptions(page, limit));
export const useWorkerStatus = () => useQuery(workerStatusQueryOptions());
export function useOrgTimezone(): string {
  const { data } = useQuery(settingsStatusQueryOptions());
  return data?.timezone ?? 'America/Sao_Paulo';
}
export const useUserPreferences = () => useQuery(userPreferencesQueryOptions());
export const useUserOauthLinks = () => useQuery(userOauthLinksQueryOptions());

export function useUpdateUserPreferences() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch: Record<string, unknown>) =>
      patchJson<Record<string, unknown>, Record<string, unknown>>(
        '/users/me/preferences',
        patch
      ),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.userPreferences(), data);
    },
  });
}

// ============================================================================
// Mutation Hooks - With automatic cache invalidation
// ============================================================================

export function useUpdatePatient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<PatientDetail> }) =>
      patchJson<PatientDetail, Partial<PatientDetail>>(`/patients/${id}`, data),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.patients() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.patientDetail(variables.id) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard() });
    },
  });
}

export function useUpdatePatientStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: number; status: 'new' | 'in_review' | 'scheduled' | 'done' }) =>
      patchJson<PatientDetail, { status: string }>(`/patients/${id}/status`, { status }),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.patients() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.patientDetail(variables.id) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.patientEyeSummary(variables.id) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard() });
    },
  });
}

export function useUpdatePatientFields() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, fields }: { id: number; fields: Partial<ExtractedFields> }) =>
      putJson<{ success: boolean }, { fields: Partial<ExtractedFields> }>(
        `/patients/${id}/fields`,
        { fields }
      ),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.patientDetail(variables.id) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.patients() });
    },
  });
}

export function useSchedulePatient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      scheduledAt,
      calendarId,
    }: {
      id: number;
      scheduledAt: string;
      calendarId?: string;
    }) =>
      patchJson<PatientDetail, { scheduledAt: string; calendarId?: string }>(
        `/patients/${id}/schedule`,
        { scheduledAt, calendarId }
      ),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.patients() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.patientDetail(variables.id) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard() });
    },
  });
}

export function useBulkSchedule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { patientIds: number[]; scheduledAt: string; calendarId?: string }) =>
      postJson<{ updated: number }, typeof data>('/patients/schedule/bulk', data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.patients() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard() });
    },
  });
}

export function useDeletePatient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      deleteJson<{ success: boolean; deletedImages?: number }>(`/patients/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.patients() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard() });
    },
  });
}

export function useLinkImage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ imageId, patientId }: { imageId: number; patientId: number | null }) =>
      patchJson<ImageSummary, { patientId: number | null }>(`/images/${imageId}/link`, {
        patientId,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.images() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.patients() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard() });
    },
  });
}

export function useBulkLinkImages() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { imageIds: number[]; patientId: number }) =>
      postJson<{ linked: number }, typeof data>('/images/bulk-link', data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.images() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.patients() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard() });
    },
  });
}

export function useReanalyzeImage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (imageId: number) =>
      postJson<{ jobId: string }, Record<string, never>>(`/images/${imageId}/reanalyze`, {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.images() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.jobs() });
    },
  });
}

export function useTriggerJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (jobType: string) =>
      postJson<{ triggered: boolean }, Record<string, never>>(`/worker/trigger/${jobType}`, {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.worker() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.jobs() });
    },
  });
}

// ============================================================================
// Surgical Lists — Query Options & Hooks
// ============================================================================

export const surgicalListsQueryOptions = (filters?: { dateFrom?: string; dateTo?: string; status?: string }) =>
  queryOptions({
    queryKey: queryKeys.surgicalListsList(filters),
    queryFn: () => {
      const params = new URLSearchParams();
      if (filters?.dateFrom) params.set('dateFrom', filters.dateFrom);
      if (filters?.dateTo) params.set('dateTo', filters.dateTo);
      if (filters?.status) params.set('status', filters.status);
      const qs = params.toString();
      return getJson<SurgicalListSummary[]>(`/surgical-lists${qs ? `?${qs}` : ''}`);
    },
    staleTime: 1000 * 60 * 2,
  });

export const surgicalListDetailQueryOptions = (id: string) =>
  queryOptions({
    queryKey: queryKeys.surgicalListDetail(id),
    queryFn: () => getJson<SurgicalListDetail>(`/surgical-lists/${id}`),
    enabled: !!id,
    staleTime: 1000 * 60 * 2,
  });

export const useSurgicalLists = (filters?: { dateFrom?: string; dateTo?: string; status?: string }) =>
  useQuery(surgicalListsQueryOptions(filters));

export const useSurgicalListDetail = (id: string, options?: { enabled?: boolean }) =>
  useQuery({
    ...surgicalListDetailQueryOptions(id),
    enabled: !!id && (options?.enabled ?? true),
  });

export function useCreateSurgicalList() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateSurgicalListRequest) =>
      postJson<SurgicalListDetail, CreateSurgicalListRequest>('/surgical-lists', data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.surgicalLists() });
    },
  });
}

export function useUpdateSurgicalList() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateSurgicalListRequest }) =>
      putJson<SurgicalListDetail, UpdateSurgicalListRequest>(`/surgical-lists/${id}`, data),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.surgicalLists() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.surgicalListDetail(variables.id) });
    },
  });
}

export function useDeleteSurgicalList() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      deleteJson<void>(`/surgical-lists/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.surgicalLists() });
    },
  });
}

export function useAddSurgicalListItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ listId, data }: { listId: string; data: CreateSurgicalListItemRequest }) =>
      postJson<SurgicalListDetail, CreateSurgicalListItemRequest>(
        `/surgical-lists/${listId}/items`,
        data
      ),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.surgicalLists() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.surgicalListDetail(variables.listId) });
    },
  });
}

export function useUpdateSurgicalListItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      listId,
      itemId,
      data,
    }: {
      listId: string;
      itemId: string;
      data: UpdateSurgicalListItemRequest;
    }) =>
      putJson<SurgicalListDetail, UpdateSurgicalListItemRequest>(
        `/surgical-lists/${listId}/items/${itemId}`,
        data
      ),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.surgicalLists() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.surgicalListDetail(variables.listId) });
    },
  });
}

export function useRemoveSurgicalListItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ listId, itemId }: { listId: string; itemId: string }) =>
      deleteJson<SurgicalListDetail>(`/surgical-lists/${listId}/items/${itemId}`),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.surgicalLists() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.surgicalListDetail(variables.listId) });
    },
  });
}

export function useValidateSurgicalList() {
  return useMutation({
    mutationFn: (id: string) =>
      postJson<ValidationResponse, Record<string, never>>(`/surgical-lists/${id}/validate`, {}),
  });
}

export function useUpdateSurgicalListStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: ListStatus }) =>
      putJson<SurgicalListDetail, UpdateSurgicalListRequest>(`/surgical-lists/${id}`, { status }),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.surgicalLists() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.surgicalListDetail(variables.id) });
    },
  });
}

export function useReorderListItems() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ listId, patientIds }: { listId: string; patientIds: string[] }) =>
      putJson<SurgicalListDetail, { patientIds: string[] }>(
        `/surgical-lists/${listId}/reorder`,
        { patientIds },
      ),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: [...queryKeys.patients(), 'paginated'] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.surgicalListDetail(variables.listId) });
    },
  });
}

// ============================================================================
// Patient Images & Transcription Hooks
// ============================================================================

export function usePatientImages(patientId: number | null) {
  return useQuery({
    queryKey: [...queryKeys.patients(), patientId, 'images'],
    queryFn: () => getJson<PatientImageSummary[]>(`/patients/${patientId}/images`),
    enabled: patientId !== null && patientId > 0,
    // Cycle 3 v7.29.0: poll a cada 4s enquanto houver imagem em SCANNING.
    // Sem isso, operador via spinner congelado ate fechar/reabrir o modal.
    // Pii-scan tipico ~10-15s pelo worker. 4s e tradeoff razoavel responsividade
    // vs carga (so dispara enquanto ha SCANNING; CLEAN/PII_DETECTED para o poll).
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!Array.isArray(data)) return false;
      const hasScanning = data.some((img) => img.piiStatus === 'SCANNING');
      return hasScanning ? 4000 : false;
    },
  });
}

export function usePatientTranscription(patientId: number | null) {
  return useQuery({
    queryKey: [...queryKeys.patients(), patientId, 'transcription'],
    queryFn: () => getJson<{ text: string } | null>(`/patients/${patientId}/transcription-text`),
    enabled: patientId !== null && patientId > 0,
  });
}

export function useCheckDuplicates() {
  return useMutation({
    mutationFn: (items: Array<{ patientId: string; eye: string }>) =>
      postJson<
        { duplicates: Array<{ patientId: string; eye: string; existingListId: string; existingListDate: string; existingListShift: string }> },
        { items: Array<{ patientId: string; eye: string }> }
      >('/surgical-lists/check-duplicates', { items }),
  });
}

// ============================================================================
// Patient Creation & Fields — Reusable hooks
// ============================================================================

export function useCreatePatient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { id: number; firstName: string; secondName?: string; age: number; sex?: string; healthPlan?: string }) =>
      postJson<PatientSummary, typeof data>('/patients', data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.patients() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard() });
    },
  });
}

export function useSavePatientFields() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, fields }: { id: number; fields: ExtractedFields }) =>
      putJson<PatientFields, { fields: ExtractedFields; source: 'manual' | 'ocr' }>(
        `/patients/${id}/fields`,
        { fields, source: 'manual' }
      ),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.patientDetail(variables.id) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.patients() });
    },
  });
}

// ============================================================================
// Transcription - Submit text for async processing via worker
// ============================================================================

interface TranscriptionResponse {
  jobId: string;
  status: 'queued';
}

export function useResolveConflicts() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ patientId, resolutions, manualValues }: {
      patientId: number;
      resolutions: Record<string, 'ocr' | 'transcription' | 'manual'>;
      manualValues?: Record<string, string>;
    }) =>
      patchJson<{ success: boolean }, {
        resolutions: Record<string, 'ocr' | 'transcription' | 'manual'>;
        manualValues?: Record<string, string>;
      }>(
        `/patients/${patientId}/resolve-conflicts`,
        { resolutions, manualValues }
      ),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.patientDetail(variables.patientId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.patientEyeSummary(variables.patientId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.patients() });
    },
  });
}

export function usePatchEyeFields() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ patientId, patch }: { patientId: number; patch: PatchEyeFieldsRequest }) =>
      patchJson<PatientFields, PatchEyeFieldsRequest>(
        `/patients/${patientId}/eye-fields`,
        patch
      ),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.patientDetail(variables.patientId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.patientEyeSummary(variables.patientId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.patients() });
    },
  });
}

export function useSubmitTranscription() {
  return useMutation({
    mutationFn: ({ text, patientId }: { text: string; patientId: number }) =>
      postJson<TranscriptionResponse, { text: string; patientId: number }>(
        '/ocr/transcription',
        { text, patientId }
      ),
  });
}

export function useJobStatus(jobId: string | null) {
  return useQuery({
    queryKey: [...queryKeys.jobs(), 'detail', jobId],
    queryFn: () => getJson<JobSummary>(`/jobs/${jobId}`),
    enabled: !!jobId,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === 'done' || status === 'failed') return false;
      return 2000;
    },
  });
}

// ============================================================================
// Members — Query Options & Hooks
// ============================================================================

function buildMemberFilterParams(filter?: MemberFilter): URLSearchParams {
  const params = new URLSearchParams();
  if (filter?.levelIds?.length) params.set('levelIds', filter.levelIds.join(','));
  if (filter?.canSupervise != null) params.set('canSupervise', String(filter.canSupervise));
  if (filter?.canBePrimary != null) params.set('canBePrimary', String(filter.canBePrimary));
  return params;
}

export function useLevels() {
  return useQuery({
    queryKey: queryKeys.levels(),
    queryFn: () => getJson<MemberLevel[]>('/levels'),
    staleTime: 1000 * 60 * 10,
  });
}

export function useCreateLevel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Omit<MemberLevel, 'id' | 'sortOrder'>) =>
      postJson<{ id: number }, typeof data>('/levels', data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.levels() });
    },
  });
}

export function useUpdateLevel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: number } & Partial<Omit<MemberLevel, 'id' | 'sortOrder'>>) =>
      putJson<void, typeof data>(`/levels/${id}`, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.levels() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.members() });
    },
  });
}

export function useDeleteLevel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteJson(`/levels/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.levels() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.members() });
    },
  });
}

export function useReorderLevels() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: number[]) =>
      putJson<void, { ids: number[] }>('/levels/reorder', { ids }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.levels() });
    },
  });
}

export function useMemberSearch(q: string, filter?: MemberFilter) {
  return useQuery({
    queryKey: queryKeys.memberSearch(q, filter),
    queryFn: () => {
      const params = buildMemberFilterParams(filter);
      if (q) params.set('q', q);
      return getJson<Member[]>(`/members/search?${params}`);
    },
    staleTime: 1000 * 60 * 2,
  });
}

export function useMembers(filter?: MemberFilter) {
  return useQuery({
    queryKey: queryKeys.membersList(filter),
    queryFn: () => {
      const params = buildMemberFilterParams(filter);
      const qs = params.toString();
      return getJson<Member[]>(`/members${qs ? `?${qs}` : ''}`);
    },
    staleTime: 1000 * 60 * 5,
  });
}

export function useCreateMember() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string; levelId: number; registration?: string }) =>
      postJson<Member, { name: string; levelId: number; registration?: string }>('/members', data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.members() });
    },
  });
}

export function useUpdateMember() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<{ name: string; levelId: number; registration: string }> }) =>
      patchJson<Member, typeof data>(`/members/${id}`, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.members() });
    },
  });
}

export function useDeleteMember() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteJson(`/members/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.members() });
    },
  });
}

// ============================================================================
// Create With Items & Batch Add — Hooks
// ============================================================================

interface CreateWithItemsRequest {
  date?: string;
  shift?: string;
  scheduledAt?: string;
  avgProcedureDuration?: number;
  type?: ListType;
  residentId?: number | null;
  preceptorId?: number | null;
  activityTypeId?: number;
  protocolId?: number;
  notes?: string;
  items: Array<{
    patientId: string;
    // EXK-46: eye is nullable when protocol.eyeRequired === false or listType !== 'CIRURGIA'.
    // BE DTO accepts null via @ValidateIf; service validates via isEyeRequired().
    eye: EyeSide | null;
    procedure?: string;
    stage?: SurgeryStage;
    notesWall?: string;
  }>;
}

export function useCreateSurgicalListWithItems() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateWithItemsRequest) =>
      postJson<SurgicalListDetail, CreateWithItemsRequest>('/surgical-lists/create-with-items', data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.surgicalLists() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.patients() });
    },
  });
}

export function useAddItemsBatch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ listId, items }: {
      listId: string;
      // EXK-46: eye nullable to match BE @ValidateIf; service validates per protocol/listType.
      items: Array<{ patientId: string; eye: EyeSide | null; procedure?: string; stage?: SurgeryStage; notesWall?: string }>;
    }) =>
      postJson<SurgicalListDetail, { items: typeof items }>(`/surgical-lists/${listId}/items/batch`, { items }),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.surgicalLists() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.surgicalListDetail(variables.listId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.patients() });
    },
  });
}

// ============================================================================
// Patient Search Hook
// ============================================================================

export function usePatientSearch(q: string) {
  return useQuery({
    queryKey: queryKeys.patientSearch(q),
    queryFn: () => getJson<PatientSummary[]>(`/patients/search?q=${encodeURIComponent(q)}`),
    enabled: q.length >= 2,
    staleTime: 1000 * 60 * 1,
  });
}

// ============================================================================
// Logs
// ============================================================================

interface LogStats {
  total: number;
  errors: number;
  warnings: number;
  uptime: string;
  memoryMB: number;
  bufferSize: number;
}

export function useLogStats(enabled = true) {
  return useQuery({
    queryKey: queryKeys.logStats(),
    queryFn: () => getJson<LogStats>('/logs/stats'),
    staleTime: 10_000,
    refetchInterval: 10_000,
    enabled,
  });
}

export function useLogSources(enabled = true) {
  return useQuery({
    queryKey: queryKeys.logSources(),
    queryFn: () => getJson<string[]>('/logs/sources'),
    staleTime: 30_000,
    enabled,
  });
}

// ============================================================================
// Document Types — Query Options & Hooks
// ============================================================================

export const documentTypesQueryOptions = () =>
  queryOptions({
    queryKey: queryKeys.documentTypes(),
    queryFn: () => getJson<DocumentType[]>('/document-types'),
    staleTime: 1000 * 60 * 5,
  });

export const useDocumentTypes = () => useQuery(documentTypesQueryOptions());

export function useCreateDocumentType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string; slug: string }) =>
      postJson<DocumentType, { name: string; slug: string }>('/document-types', data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.documentTypes() });
    },
  });
}

export function useUpdateDocumentType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: { name?: string; slug?: string } }) =>
      patchJson<DocumentType, { name?: string; slug?: string }>(`/document-types/${id}`, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.documentTypes() });
    },
  });
}

export function useDeleteDocumentType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      deleteJson<void>(`/document-types/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.documentTypes() });
    },
  });
}

// ============================================================================
// Absence Types — Query Options & Hooks (EXK-344)
// ============================================================================

export const absenceTypesQueryOptions = () =>
  queryOptions({
    queryKey: queryKeys.absenceTypes(),
    queryFn: () => getJson<AbsenceType[]>('/absence-types'),
    staleTime: 1000 * 60 * 5,
  });

export const useAbsenceTypes = () => useQuery(absenceTypesQueryOptions());

export function useCreateAbsenceType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: AbsenceTypeFormValues) =>
      postJson<AbsenceType, AbsenceTypeFormValues>('/absence-types', data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.absenceTypes() });
    },
  });
}

export function useUpdateAbsenceType() {
  const queryClient = useQueryClient();
  return useMutation({
    // BE expõe @Put(":id") com DTO exigindo code/name/color obrigatórios
    // (icon/sortOrder opcionais). Sempre enviar payload completo — Partial<>
    // seria contrato falso (BE rejeita com 400 via class-validator).
    mutationFn: ({ id, data }: { id: number; data: AbsenceTypeFormValues }) =>
      putJson<AbsenceType, AbsenceTypeFormValues>(`/absence-types/${id}`, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.absenceTypes() });
    },
  });
}

export function useDeleteAbsenceType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      deleteJson<void>(`/absence-types/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.absenceTypes() });
    },
  });
}

// ============================================================================
// Appointment Subtypes — Query Options & Hooks
// ============================================================================

export const appointmentSubtypesQueryOptions = () =>
  queryOptions({
    queryKey: queryKeys.appointmentSubtypes(),
    queryFn: () => getJson<AppointmentSubtype[]>('/appointment-subtypes'),
    staleTime: 1000 * 60 * 5,
  });

export const useAppointmentSubtypes = () => useQuery(appointmentSubtypesQueryOptions());

export function useCreateAppointmentSubtype() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string; slug: string; active?: boolean }) =>
      postJson<AppointmentSubtype, { name: string; slug: string; active?: boolean }>('/appointment-subtypes', data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.appointmentSubtypes() });
    },
  });
}

export function useUpdateAppointmentSubtype() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: { name?: string; slug?: string; active?: boolean } }) =>
      patchJson<AppointmentSubtype, { name?: string; slug?: string; active?: boolean }>(`/appointment-subtypes/${id}`, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.appointmentSubtypes() });
    },
  });
}

export function useDeleteAppointmentSubtype() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      deleteJson<void>(`/appointment-subtypes/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.appointmentSubtypes() });
    },
  });
}

// ============================================================================
// Scheduling Config — Query Options & Hooks
// ============================================================================

export const schedulingConfigQueryOptions = () =>
  queryOptions({
    queryKey: queryKeys.schedulingConfig(),
    queryFn: () => getJson<SchedulingConfig>('/scheduling-config'),
    staleTime: 1000 * 60 * 5,
  });

export const useSchedulingConfig = () => useQuery(schedulingConfigQueryOptions());

export function useUpdateSchedulingConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<SchedulingConfig>) =>
      patchJson<SchedulingConfig, Partial<SchedulingConfig>>('/scheduling-config', data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.schedulingConfig() });
    },
  });
}

// ============================================================================
// Patient Appointments — Query Options & Hooks
// ============================================================================

export const patientAppointmentsQueryOptions = (patientId: number) =>
  queryOptions({
    queryKey: queryKeys.patientAppointments(patientId),
    queryFn: () => getJson<PatientAppointment[]>(`/patients/${patientId}/appointments`),
    enabled: patientId > 0,
    staleTime: 1000 * 60 * 2,
  });

export const usePatientAppointments = (patientId: number) =>
  useQuery(patientAppointmentsQueryOptions(patientId));

// ============================================================================
// Patient Timeline — Query Options & Hooks
// ============================================================================

export const patientTimelineQueryOptions = (patientId: number) =>
  queryOptions({
    queryKey: queryKeys.patientTimeline(patientId),
    queryFn: () => getJson<TimelineGroup[]>(`/patients/${patientId}/timeline`),
    enabled: patientId > 0,
    staleTime: 1000 * 60 * 2,
  });

export const usePatientTimeline = (patientId: number) =>
  useQuery(patientTimelineQueryOptions(patientId));

// ============================================================================
// Schedule Returns — Mutation Hook
// ============================================================================

export function useScheduleReturns() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ listId, data }: { listId: string; data: ScheduleReturnsRequest }) =>
      postJson<ScheduleReturnsResponse, ScheduleReturnsRequest>(
        `/surgical-lists/${listId}/schedule-returns`,
        data,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.surgicalLists() });
    },
  });
}

// ============================================================================
// Panoramic Calendar — Cross-org read-only view
// ============================================================================

export interface PanoramicEvent {
  id: number;
  orgId: number;
  orgName: string;
  date: string;
  shift: string;
  type: string;
  status: string;
  residentName: string | null;
  preceptorName: string | null;
  itemCount: number;
}

export function usePanoramicCalendar(dateFrom?: string, dateTo?: string) {
  return useQuery({
    queryKey: queryKeys.panoramic(dateFrom, dateTo),
    queryFn: () => {
      const params = new URLSearchParams();
      if (dateFrom) params.set('dateFrom', dateFrom);
      if (dateTo) params.set('dateTo', dateTo);
      const qs = params.toString();
      return getJson<PanoramicEvent[]>(`/calendar/panoramic${qs ? `?${qs}` : ''}`);
    },
    staleTime: 1000 * 60 * 2,
  });
}

// ============================================================================
// Member Groups — Query Options & Hooks
// ============================================================================

export const memberGroupsQueryOptions = () =>
  queryOptions({
    queryKey: queryKeys.memberGroups(),
    queryFn: () => getJson<MemberGroupSummary[]>('/member-groups'),
    staleTime: 1000 * 60 * 2,
  });

export const memberGroupMembersQueryOptions = (groupId: number | undefined) =>
  queryOptions({
    queryKey: queryKeys.memberGroupMembers(groupId ?? 0),
    queryFn: () => getJson<MemberGroupMember[]>(`/member-groups/${groupId}/members`),
    // Cycle 6 v7.32.0: aceita undefined ao inves de sentinel 0 que criava
    // cache key espuria ['...member-groups', 0, 'members'] mesmo nunca
    // fetching (zumbi cache reativavel se invalidate accidental com 0).
    enabled: groupId !== undefined && groupId > 0,
    staleTime: 1000 * 60 * 2,
  });

export const useMemberGroups = () => useQuery(memberGroupsQueryOptions());

export const useMemberGroupMembers = (groupId: number | undefined) =>
  useQuery(memberGroupMembersQueryOptions(groupId));

export function useCreateMemberGroup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string; color?: string }) =>
      postJson<MemberGroupSummary, { name: string; color?: string }>('/member-groups', data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.memberGroups() });
    },
  });
}

export function useUpdateMemberGroup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: { name?: string; color?: string } }) =>
      putJson<MemberGroupSummary, { name?: string; color?: string }>(`/member-groups/${id}`, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.memberGroups() });
    },
  });
}

export function useDeleteMemberGroup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      deleteJson<void>(`/member-groups/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.memberGroups() });
    },
  });
}

export function useAddGroupMember() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ groupId, memberId }: { groupId: number; memberId: number }) =>
      postJson<MemberGroupMember, { memberId: number }>(`/member-groups/${groupId}/members`, { memberId }),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.memberGroupMembers(variables.groupId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.memberGroups() });
    },
  });
}

export function useRemoveGroupMember() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ groupId, memberId }: { groupId: number; memberId: number }) =>
      deleteJson<void>(`/member-groups/${groupId}/members/${memberId}`),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.memberGroupMembers(variables.groupId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.memberGroups() });
    },
  });
}

// ============================================================================
// Activity Types — Query Options & Hooks
// ============================================================================

export const activityTypesQueryOptions = () =>
  queryOptions({
    queryKey: queryKeys.activityTypes(),
    queryFn: () => getJson<ActivityTypeSummary[]>('/activity-types'),
    staleTime: 1000 * 60 * 2,
  });

export const useActivityTypes = () => useQuery(activityTypesQueryOptions());

export function useCreateActivityType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string; abbreviation: string; color: string; availableForScheduling?: boolean }) =>
      postJson<ActivityTypeSummary, typeof data>('/activity-types', data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.activityTypes() });
    },
  });
}

export function useUpdateActivityType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: { name?: string; abbreviation?: string; color?: string; availableForScheduling?: boolean } }) =>
      putJson<ActivityTypeSummary, typeof data>(`/activity-types/${id}`, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.activityTypes() });
    },
  });
}

export function useDeleteActivityType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      deleteJson<void>(`/activity-types/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.activityTypes() });
    },
  });
}

export function useReorderActivityTypes() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: number[]) =>
      putJson<void, { ids: number[] }>('/activity-types/reorder', { ids }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.activityTypes() });
    },
  });
}

// ============================================================================
// Rotation Template — Query Options & Hooks
// ============================================================================

export const rotationTemplateQueryOptions = () =>
  queryOptions({
    queryKey: queryKeys.rotationTemplate(),
    queryFn: () => getJson<{ slots: RotationSlotSummary[] }>('/rotation/template'),
    // Cycle 7 H3 (manual-fix 004): 2min → 30s. Admin-A muda template,
    // admin-B esperava 2min pra ver mudança — tradeoff perf/concurrency
    // pendia pra perf, mas concurrency é o caso real (rotation tem
    // multi-admin edit). Invalidate explícito após save já existe.
    staleTime: 1000 * 30,
  });

export const useRotationTemplate = () => useQuery(rotationTemplateQueryOptions());

export function useSaveRotationTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { slots: RotationSlotSummary[] }) =>
      putJson<{ slots: RotationSlotSummary[] }, typeof data>('/rotation/template', data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.rotationTemplate() });
      void queryClient.invalidateQueries({ queryKey: [...queryKeys.all(), 'rotation', 'calendar'] });
    },
  });
}

// ============================================================================
// Rotation Calendar — Query Options & Hooks
// ============================================================================

export const rotationCalendarQueryOptions = (from: string, to: string, options?: { levelIds?: number[] }) => {
  const params = new URLSearchParams({ from, to });
  if (options?.levelIds?.length) {
    params.set('levelIds', options.levelIds.join(','));
  }
  return queryOptions({
    queryKey: [...queryKeys.rotationCalendar(from, to), options?.levelIds ?? []] as const,
    queryFn: () => getJson<ProjectedDay[]>(`/rotation/calendar?${params}`),
    enabled: !!from && !!to,
    // Cycle 7 H4 (manual-fix 004): 2min → 30s.
    staleTime: 1000 * 30,
  });
};

export const useRotationCalendar = (from: string, to: string, options?: { levelIds?: number[] }) =>
  useQuery(rotationCalendarQueryOptions(from, to, options));

// ============================================================================
// Rotation Availability — Query Options & Hooks
// ============================================================================

export const rotationAvailabilityQueryOptions = (from: string, to: string, activityType?: string) =>
  queryOptions({
    queryKey: queryKeys.rotationAvailability(from, to, activityType),
    queryFn: () => {
      const params = new URLSearchParams();
      params.set('from', from);
      params.set('to', to);
      if (activityType) params.set('activityType', activityType);
      return getJson<AvailableSlot[]>(`/rotation/availability?${params}`);
    },
    enabled: !!from && !!to,
    // Cycle 7 (manual-fix 004): 2min → 30s — vagas mudam quando admin
    // edita override, esperar 2min frustra UX.
    staleTime: 1000 * 30,
  });

export const useRotationAvailability = (from: string, to: string, activityType?: string) =>
  useQuery(rotationAvailabilityQueryOptions(from, to, activityType));

// ============================================================================
// Rotation Overrides — Query Options & Hooks
// ============================================================================

export const rotationOverridesQueryOptions = (from?: string, to?: string) =>
  queryOptions({
    queryKey: queryKeys.rotationOverrides(from, to),
    queryFn: () => {
      const params = new URLSearchParams();
      if (from) params.set('from', from);
      if (to) params.set('to', to);
      const qs = params.toString();
      return getJson<RotationOverrideSummary[]>(`/rotation/overrides${qs ? `?${qs}` : ''}`);
    },
    // Cycle 7 (manual-fix 004): 2min → 30s.
    staleTime: 1000 * 30,
  });

export const useRotationOverrides = (from?: string, to?: string) =>
  useQuery(rotationOverridesQueryOptions(from, to));

export function useCreateRotationOverride() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      date: string;
      shift?: string;
      // EXK-343: inclui ABSENCE (drift do BE corrigido).
      type: RotationOverrideType;
      slotId?: number;
      originalMemberId?: number;
      replacementMemberId?: number;
      notes?: string;
      levelId?: number;
      // EXK-43: events carry participants + location (no slot/member assignment).
      // EXK-343: ABSENCE também carrega participants.
      extraData?: { participants?: number[]; location?: string };
      // EXK-348: FK pra absence_types. Só relevante quando type='ABSENCE'.
      // null = ABSENCE genérico (legacy ou tipo não selecionado). BE aceita
      // desde dominoris-api v3.39.x (rotation.dto.ts:118).
      absenceTypeId?: number | null;
    }) =>
      // EXK-348 /qa round 1 (codex P1): BE returns `{ id }`, not the full
      // RotationOverrideSummary (rotation.controller.ts:93).
      postJson<{ id: number }, typeof data>('/rotation/overrides', data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [...queryKeys.all(), 'rotation', 'overrides'] });
      void queryClient.invalidateQueries({ queryKey: [...queryKeys.all(), 'rotation', 'calendar'] });
    },
  });
}

export function useUpdateRotationOverride() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: {
      id: number;
      data: {
        date?: string;
        shift?: string;
        // EXK-343: inclui EVENT e ABSENCE (drift do BE corrigido — useUpdate
        // aceitava só BCSH antes).
        type?: RotationOverrideType;
        slotId?: number;
        originalMemberId?: number;
        replacementMemberId?: number;
        notes?: string;
        // EXK-348: edit ABSENCE precisa do mesmo vocabulário do create.
        // BE aceita absenceTypeId + extraData no PUT (mesmo DTO do POST).
        extraData?: { participants?: number[]; location?: string };
        absenceTypeId?: number | null;
      };
    }) =>
      // EXK-348 /qa round 1 (codex P1): BE returns `void` on PUT (the
      // controller has no `@Body() return` — rotation.controller.ts:93).
      putJson<void, typeof data>(`/rotation/overrides/${id}`, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [...queryKeys.all(), 'rotation', 'overrides'] });
      void queryClient.invalidateQueries({ queryKey: [...queryKeys.all(), 'rotation', 'calendar'] });
    },
  });
}

export function useDeleteRotationOverride() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      deleteJson<void>(`/rotation/overrides/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [...queryKeys.all(), 'rotation', 'overrides'] });
      void queryClient.invalidateQueries({ queryKey: [...queryKeys.all(), 'rotation', 'calendar'] });
    },
  });
}

// ============================================================================
// Follow-Up Protocols — Query Options & Hooks
// ============================================================================

export const followUpProtocolsQueryOptions = () =>
  queryOptions({
    queryKey: queryKeys.followUpProtocols(),
    queryFn: () => getJson<FollowUpProtocolSummary[]>('/follow-up-protocols'),
    // Cycle 5 H3 (manual-fix 004): 2min → 30s. isDefault flag tem
    // concurrency real (admin-A muda default, admin-B vê stale por
    // 2min). Invalidate explícito já existe nos 3 mutations abaixo.
    staleTime: 1000 * 30,
  });

export const useFollowUpProtocols = () => useQuery(followUpProtocolsQueryOptions());

export function useCreateFollowUpProtocol() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string; isDefault?: boolean; steps: Array<{ stepOrder: number; name: string; targetDays: number; toleranceDays: number; requiredActivityTypeId?: number }> }) =>
      postJson<FollowUpProtocolSummary, typeof data>('/follow-up-protocols', data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.followUpProtocols() });
    },
  });
}

export function useUpdateFollowUpProtocol() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: { name?: string; isDefault?: boolean; steps?: Array<{ stepOrder: number; name: string; targetDays: number; toleranceDays: number; requiredActivityTypeId?: number }> } }) =>
      putJson<FollowUpProtocolSummary, typeof data>(`/follow-up-protocols/${id}`, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.followUpProtocols() });
    },
  });
}

export function useDeleteFollowUpProtocol() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      deleteJson<void>(`/follow-up-protocols/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.followUpProtocols() });
    },
  });
}

// ============================================================================
// Finalize List — Mutation Hook
// ============================================================================

export function useFinalizeList() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      id: string;
      attendance: Array<{ itemId: number; status: string; finalizeNotes?: string }>;
      protocolId?: number;
      // Manual-fix 009 (L-001): operator-acknowledged validation ALERTs.
      // Sent to audit metadata as proof of conscious confirmation when
      // finalizing a list that has soft alerts (e.g. LIO out of usual range).
      alertOverrides?: Array<{ code: string; justification?: string }>;
      // EXK-45 Q2: earlyOverride — registrar antecipado sem AAL3.
      // Audit simples: motivo obrigatório, reversível (≠ reopen FINAL terminal).
      earlyOverride?: { reason: string };
    }) =>
      postJson<
        FinalizeResponse,
        {
          attendance: Array<{ itemId: number; status: string; finalizeNotes?: string }>;
          protocolId?: number;
          alertOverrides?: Array<{ code: string; justification?: string }>;
          earlyOverride?: { reason: string };
        }
      >(
        `/surgical-lists/${data.id}/finalize`,
        {
          attendance: data.attendance,
          protocolId: data.protocolId,
          alertOverrides: data.alertOverrides,
          earlyOverride: data.earlyOverride,
        },
      ),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.surgicalLists() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.surgicalListDetail(variables.id) });
    },
  });
}

// ============================================================================
// Evolution Notes — Query Options & Hooks
// ============================================================================

export const evolutionNotesQueryOptions = (itemId: number) =>
  queryOptions({
    queryKey: queryKeys.evolutionNotes(itemId),
    queryFn: () => getJson<EvolutionNoteSummary[]>(`/evolution-notes?itemId=${itemId}`),
    enabled: itemId > 0,
    staleTime: 1000 * 60 * 2,
  });

export const patientEvolutionsQueryOptions = (patientId: number, from?: string, to?: string) =>
  queryOptions({
    queryKey: queryKeys.patientEvolutions(patientId, from, to),
    queryFn: () => {
      const params = new URLSearchParams();
      if (from) params.set('from', from);
      if (to) params.set('to', to);
      const qs = params.toString();
      return getJson<EvolutionNoteSummary[]>(`/evolution-notes/patient/${patientId}${qs ? `?${qs}` : ''}`);
    },
    enabled: patientId > 0,
    staleTime: 1000 * 60 * 2,
  });

export const useEvolutionNotes = (itemId: number) => useQuery(evolutionNotesQueryOptions(itemId));

export const usePatientEvolutions = (patientId: number, from?: string, to?: string) =>
  useQuery(patientEvolutionsQueryOptions(patientId, from, to));

export function useCreateTextEvolution() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { itemId: number; content: string }) =>
      postJson<EvolutionNoteSummary, typeof data>('/evolution-notes/text', data),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.evolutionNotes(variables.itemId) });
    },
  });
}

export function useUploadEvolution() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, file }: { itemId: number; file: File }) => {
      const formData = new FormData();
      formData.append('itemId', String(itemId));
      formData.append('file', file);
      return postForm<EvolutionNoteSummary>('/evolution-notes/upload', formData);
    },
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.evolutionNotes(variables.itemId) });
    },
  });
}

export function useDeleteEvolution() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      deleteJson<void>(`/evolution-notes/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [...queryKeys.all(), 'evolution-notes'] });
      void queryClient.invalidateQueries({ queryKey: [...queryKeys.all(), 'patient-evolutions'] });
    },
  });
}

// ============================================================================
// Stage Types — Query Options & Hooks (Phase 3)
// ============================================================================

export const stageTypesQueryOptions = () =>
  queryOptions({
    queryKey: queryKeys.stageTypes(),
    queryFn: () => getJson<StageTypeSummary[]>('/stage-types'),
    staleTime: 1000 * 60 * 5,
  });

export const useStageTypes = () => useQuery(stageTypesQueryOptions());

export function useCreateStageType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string; slug: string; description?: string; clinicalAlert?: boolean; sortOrder?: number }) =>
      postJson<StageTypeSummary, typeof data>('/stage-types', data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.stageTypes() });
    },
  });
}

export function useUpdateStageType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: { name?: string; slug?: string; description?: string; clinicalAlert?: boolean; sortOrder?: number } }) =>
      putJson<StageTypeSummary, typeof data>(`/stage-types/${id}`, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.stageTypes() });
    },
  });
}

export function useDeleteStageType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      deleteJson<void>(`/stage-types/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.stageTypes() });
    },
  });
}

// ============================================================================
// PII Detection (LGPD) — Query Options & Hooks
// ============================================================================

export const piiConfigQueryOptions = () =>
  queryOptions({
    queryKey: queryKeys.piiConfig(),
    queryFn: () => getJson<PiiConfig>('/pii/config'),
    staleTime: 1000 * 60 * 5,
  });

export const piiDashboardQueryOptions = () =>
  queryOptions({
    queryKey: queryKeys.piiDashboard(),
    queryFn: () => getJson<PiiDashboard>('/pii/dashboard'),
    staleTime: 1000 * 60 * 2,
  });

export const piiImagesQueryOptions = (status: PiiStatus) =>
  queryOptions({
    queryKey: queryKeys.piiImages(status),
    queryFn: () => getJson<PiiImageSummary[]>(`/pii/images?status=${status}`),
    staleTime: 1000 * 60 * 2,
  });

export const usePiiConfig = () => useQuery(piiConfigQueryOptions());
export const usePiiDashboard = () => useQuery(piiDashboardQueryOptions());
export const usePiiImages = (status: PiiStatus) => useQuery(piiImagesQueryOptions(status));

export function useUpdatePiiConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (config: Partial<PiiConfig>) =>
      putJson<PiiConfig, Partial<PiiConfig>>('/pii/config', config),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.piiConfig() });
    },
  });
}

export function useTriggerPiiScan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (imageId: number) =>
      postJson<{ status: string }, Record<string, never>>(`/pii/${imageId}/scan`, {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.piiDashboard() });
    },
  });
}

// Invalidate every cache touched by a PII state transition (dashboard +
// status-filtered pii-images lists + patient detail/lists).
function invalidatePiiCaches(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: queryKeys.piiDashboard() });
  void queryClient.invalidateQueries({ queryKey: [...queryKeys.all(), 'pii-images'] });
  void queryClient.invalidateQueries({ queryKey: queryKeys.patients() });
}

export function useMarkSafe() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ imageId, reason }: { imageId: number; reason: string }) =>
      postJson<{ imageId: number; status: string }, { reason: string }>(`/pii/${imageId}/mark-safe`, { reason }),
    onSuccess: () => {
      invalidatePiiCaches(queryClient);
    },
  });
}

export function useRetryScan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (imageId: number) =>
      postJson<{ status: string }, Record<string, never>>(`/pii/${imageId}/retry-scan`, {}),
    onSuccess: () => {
      invalidatePiiCaches(queryClient);
    },
  });
}

export function useRetryRedact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (imageId: number) =>
      postJson<{ status: string }, Record<string, never>>(`/pii/${imageId}/retry-redact`, {}),
    onSuccess: () => {
      invalidatePiiCaches(queryClient);
    },
  });
}

export function useRedactConfirm() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (imageId: number) =>
      postJson<{ status: string }, Record<string, never>>(`/pii/${imageId}/redact-confirm`, {}),
    onSuccess: () => {
      invalidatePiiCaches(queryClient);
    },
  });
}

export function useOriginalAccess() {
  return useMutation({
    mutationFn: (imageId: number) =>
      getJson<{ signedUrl: string; expiresAt: string }>(`/pii/${imageId}/original`),
  });
}

export function useRedactPreview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (imageId: number) =>
      postJson<{ previewUrl: string }, Record<string, never>>(`/pii/${imageId}/redact-preview`, {}),
    onSuccess: () => {
      // State-changing: server transitions to REDACTION_PENDING + persists
      // preview, so all PII-aware caches must refresh (not only dashboard).
      invalidatePiiCaches(queryClient);
    },
  });
}

// Findings list for an image (Layer A + Layer B). Used by PiiResolveModal to
// drive the lateral list and the CSS bbox overlays.
// staleTime: 0 — always refetch on modal open. Stale findings would race with
// resolve submissions (finding ids could no longer match server state).
export const imageFindingsQueryOptions = (imageId: number) =>
  queryOptions({
    queryKey: queryKeys.piiFindings(imageId),
    queryFn: () => getJson<PiiFinding[]>(`/pii/${imageId}/findings`),
    staleTime: 0,
  });

export const useImageFindings = (imageId: number, enabled: boolean = true) =>
  useQuery({ ...imageFindingsQueryOptions(imageId), enabled });

// Atomic resolve: redact bbox findings + mark text findings safe in a single call.
// Backend rejects mixed (with text findings still unresolved) when caller is
// trying to redact only — but our resolve endpoint accepts both lists at once,
// so this hook keeps the modal's UX atomic.
export function useResolvePii() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ imageId, ...payload }: { imageId: number } & PiiResolveRequest) =>
      postJson<PiiResolveResponse, PiiResolveRequest>(`/pii/${imageId}/resolve`, payload),
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.piiFindings(vars.imageId) });
      invalidatePiiCaches(queryClient);
    },
  });
}

// ============================================================================
// Clinical Exams — Query Options & Hooks
// ============================================================================

export function fieldDefinitionsOptions() {
  return queryOptions({
    queryKey: queryKeys.fieldDefinitions(),
    queryFn: () => getJson<FieldDefinition[]>('/field-definitions'),
    staleTime: 5 * 60 * 1000,
  });
}
export function useFieldDefinitions() {
  return useQuery(fieldDefinitionsOptions());
}

export function useCreateFieldDefinition() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { key: string; label: string; fieldType: string; unit?: string; options?: string[]; fieldGroup?: string; hasLaterality?: boolean; sortOrder?: number }) =>
      postJson<FieldDefinition, typeof data>('/field-definitions', data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.fieldDefinitions() });
    },
  });
}

export function useUpdateFieldDefinition() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<{ key: string; label: string; fieldType: string; unit?: string; options?: string[]; fieldGroup?: string; hasLaterality?: boolean; sortOrder?: number }> }) =>
      putJson<FieldDefinition, typeof data>(`/field-definitions/${id}`, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.fieldDefinitions() });
    },
  });
}

export function useDeleteFieldDefinition() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      deleteJson<void>(`/field-definitions/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.fieldDefinitions() });
    },
  });
}

export function examTemplatesOptions() {
  return queryOptions({
    queryKey: queryKeys.examTemplates(),
    queryFn: () => getJson<ExamTemplateSummary[]>('/exam-templates'),
    staleTime: 5 * 60 * 1000,
  });
}
export function useExamTemplates() {
  return useQuery(examTemplatesOptions());
}

export function examTemplateDetailOptions(id: number) {
  return queryOptions({
    queryKey: queryKeys.examTemplateDetail(id),
    queryFn: () => getJson<ExamTemplateDetail>(`/exam-templates/${id}`),
    enabled: id > 0,
  });
}
export function useExamTemplateDetail(id: number) {
  return useQuery(examTemplateDetailOptions(id));
}

interface ExamSessionFilters {
  patientId?: number;
  status?: string;
  templateId?: number;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
}

export function examSessionsOptions(filters?: ExamSessionFilters) {
  return queryOptions({
    queryKey: queryKeys.examSessions(filters as Record<string, unknown> | undefined),
    queryFn: () => {
      const params = new URLSearchParams();
      if (filters?.patientId) params.set('patientId', String(filters.patientId));
      if (filters?.status) params.set('status', filters.status);
      if (filters?.templateId) params.set('templateId', String(filters.templateId));
      if (filters?.search) params.set('search', filters.search);
      if (filters?.dateFrom) params.set('dateFrom', filters.dateFrom);
      if (filters?.dateTo) params.set('dateTo', filters.dateTo);
      const qs = params.toString();
      return getJson<ExamSessionSummary[]>(`/exam-sessions${qs ? '?' + qs : ''}`);
    },
  });
}
export function useExamSessions(filters?: ExamSessionFilters) {
  return useQuery(examSessionsOptions(filters));
}

export function useDeleteExamSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteJson<void>(`/exam-sessions/${id}`),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: queryKeys.examSessions() }); },
  });
}

export function examSessionDetailOptions(id: number) {
  return queryOptions({
    queryKey: queryKeys.examSessionDetail(id),
    queryFn: () => getJson<ExamSessionDetail>(`/exam-sessions/${id}`),
    enabled: id > 0,
  });
}
export function useExamSessionDetail(id: number) {
  return useQuery(examSessionDetailOptions(id));
}

export function useClinicalValues(patientId: number, filters?: { fieldKey?: string; laterality?: string }) {
  return useQuery({
    queryKey: queryKeys.clinicalValues(patientId, filters as Record<string, unknown> | undefined),
    queryFn: () => {
      const params = new URLSearchParams();
      if (filters?.fieldKey) params.set('fieldKey', filters.fieldKey);
      if (filters?.laterality) params.set('laterality', filters.laterality);
      const qs = params.toString();
      return getJson<ClinicalValueEntry[]>(`/patients/${patientId}/clinical-values${qs ? '?' + qs : ''}`);
    },
    enabled: patientId > 0,
  });
}

export function useExamTimeline(patientId: number) {
  return useQuery({
    queryKey: queryKeys.timeline(patientId),
    queryFn: () => getJson<TimelineEntry[]>(`/patients/${patientId}/timeline`),
    enabled: patientId > 0,
  });
}

export function useCreateExamSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateExamSessionRequest) => postJson<{ id: number }, CreateExamSessionRequest>('/exam-sessions', data),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: queryKeys.examSessions() }); },
  });
}

export function useUpdateDraftData(sessionId: number) {
  return useMutation({
    mutationFn: (draftData: Record<string, unknown>) =>
      putJson<Record<string, unknown>, { draftData: Record<string, unknown> }>(`/exam-sessions/${sessionId}`, { draftData }),
  });
}

export function useCompleteExamSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: number) => patchJson<Record<string, unknown>, Record<string, never>>(`/exam-sessions/${sessionId}/complete`, {}),
    onSuccess: (_data, sessionId) => {
      void qc.invalidateQueries({ queryKey: queryKeys.examSessions() });
      void qc.invalidateQueries({ queryKey: queryKeys.examSessionDetail(sessionId) });
      // Invalidate all clinical-values and timeline entries under this org
      void qc.invalidateQueries({ queryKey: [...queryKeys.all(), 'clinical-values'] });
      void qc.invalidateQueries({ queryKey: [...queryKeys.all(), 'timeline'] });
    },
  });
}

// ============================================================================
// Exam Templates — Mutation Hooks
// ============================================================================

export function useCreateExamTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string; description?: string; fields: Array<{ fieldDefinitionId: number; required?: boolean; sortOrder?: number }> }) =>
      postJson<{ id: number }, typeof data>('/exam-templates', data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.examTemplates() });
    },
  });
}

export function useUpdateExamTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: { name: string; description?: string; fields: Array<{ fieldDefinitionId: number; required?: boolean; sortOrder?: number }> } }) =>
      putJson<ExamTemplateDetail, typeof data>(`/exam-templates/${id}`, data),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.examTemplates() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.examTemplateDetail(variables.id) });
    },
  });
}

export function useDeleteExamTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      deleteJson<void>(`/exam-templates/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.examTemplates() });
    },
  });
}

// ============================================================================
// Practice Engine — Query Options
// ============================================================================

export const scenariosQueryOptions = (filters?: { specialty?: string; difficulty?: string }) =>
  queryOptions({
    queryKey: queryKeys.scenariosList(filters),
    queryFn: () => {
      const params = new URLSearchParams();
      if (filters?.specialty) params.set('specialty', filters.specialty);
      if (filters?.difficulty) params.set('difficulty', filters.difficulty);
      const qs = params.toString();
      return getJson<PaginatedResponse<ScenarioSummary>>(`/scenarios${qs ? `?${qs}` : ''}`);
    },
    staleTime: 1000 * 60 * 5, // 5 minutes — scenarios rarely change
  });

export const scenarioDetailQueryOptions = (id: number) =>
  queryOptions({
    queryKey: queryKeys.scenarioDetail(id),
    queryFn: () => getJson<ScenarioDetail>(`/scenarios/${id}`),
    enabled: id > 0,
    staleTime: 1000 * 60 * 5,
  });

export interface PracticeSessionsFilters {
  status?: string;
  scenarioId?: number;
  /**
   * Cross-user query (EXK-352 G1 preceptor dashboard). Requer permission
   * backend `practice.read-others` — caller sem permission recebe 403.
   * Quando omitido, backend default = current user.
   */
  userId?: string;
  /** ISO 8601 — filtra sessões com createdAt >= dateFrom */
  dateFrom?: string;
  /** ISO 8601 — filtra sessões com createdAt <= dateTo */
  dateTo?: string;
  /** Paginação */
  page?: number;
  /** Items per page (default backend 20, max 100) */
  limit?: number;
}

export const practiceSessionsQueryOptions = (filters?: PracticeSessionsFilters) =>
  queryOptions({
    queryKey: queryKeys.practiceSessionsList(filters),
    queryFn: () => {
      const params = new URLSearchParams();
      if (filters?.status) params.set('status', filters.status);
      if (filters?.scenarioId != null) params.set('scenarioId', String(filters.scenarioId));
      if (filters?.userId) params.set('userId', filters.userId);
      if (filters?.dateFrom) params.set('dateFrom', filters.dateFrom);
      if (filters?.dateTo) params.set('dateTo', filters.dateTo);
      if (filters?.page != null) params.set('page', String(filters.page));
      if (filters?.limit != null) params.set('limit', String(filters.limit));
      const qs = params.toString();
      return getJson<PaginatedResponse<PracticeSessionSummary>>(`/practice-sessions${qs ? `?${qs}` : ''}`);
    },
    staleTime: 1000 * 60 * 2,
  });

export const practiceSessionDetailQueryOptions = (id: number) =>
  queryOptions({
    queryKey: queryKeys.practiceSession(id),
    queryFn: () => getJson<PracticeSessionDetail>(`/practice-sessions/${id}`),
    enabled: id > 0,
    staleTime: 1000 * 60 * 2,
  });

export const practiceScoreQueryOptions = (sessionId: number) =>
  queryOptions({
    queryKey: queryKeys.practiceScore(sessionId),
    queryFn: () => getJson<SessionScoreDetail>(`/practice-sessions/${sessionId}/score`),
    enabled: sessionId > 0,
    staleTime: 1000 * 60 * 10, // scores don't change
  });

export const practicePerformanceQueryOptions = () =>
  queryOptions({
    queryKey: queryKeys.practicePerformance(),
    queryFn: () => getJson<PerformanceAggregate>('/practice-sessions/my/performance'),
    staleTime: 1000 * 60 * 5,
  });

// ============================================================================
// Practice Engine — Query Hooks
// ============================================================================

export const useScenarios = (filters?: { specialty?: string; difficulty?: string }) =>
  useQuery(scenariosQueryOptions(filters));

export const useScenarioDetail = (id: number) =>
  useQuery(scenarioDetailQueryOptions(id));

export const useMyPracticeSessions = (filters?: PracticeSessionsFilters) =>
  useQuery(practiceSessionsQueryOptions(filters));

export const usePracticeSession = (id: number) =>
  useQuery(practiceSessionDetailQueryOptions(id));

export const usePracticeScore = (sessionId: number) =>
  useQuery(practiceScoreQueryOptions(sessionId));

export const usePracticePerformance = () =>
  useQuery(practicePerformanceQueryOptions());

export function useScenarioCalibration(scenarioId: number) {
  return useQuery({
    queryKey: queryKeys.scenarioCalibration(scenarioId),
    queryFn: () =>
      getJson<ScenarioCalibration>(`/practice-sessions/calibration/${scenarioId}`),
    enabled: scenarioId > 0,
    staleTime: 1000 * 60, // 1 min
  });
}

export function useScenarioTopPerformers(scenarioId: number, limit = 4) {
  return useQuery({
    queryKey: queryKeys.scenarioTopPerformers(scenarioId, limit),
    queryFn: () =>
      getJson<TopPerformersResult>(
        `/practice-sessions/top/${scenarioId}?limit=${limit}`
      ),
    enabled: scenarioId > 0,
    staleTime: 1000 * 60, // 1 min
  });
}

// ============================================================================
// Practice Engine — Mutation Hooks
// ============================================================================

export function useCreatePracticeSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreatePracticeSessionRequest) =>
      postJson<SessionCreateResult, CreatePracticeSessionRequest>(
        '/practice-sessions',
        data
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.practiceSessions() });
    },
  });
}

export function useStartPracticeSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionId, data }: { sessionId: number; data: StartSessionRequest }) =>
      patchJson<SessionStartResult, StartSessionRequest>(
        `/practice-sessions/${sessionId}/start`,
        data
      ),
    onSuccess: (_, { sessionId }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.practiceSession(sessionId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.practiceSessions() });
    },
    onError: (err: unknown, { sessionId }) => {
      if (isHttpError(err, 409)) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.practiceSession(sessionId) });
      }
    },
  });
}

export function useSubmitDecision() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      sessionId,
      data,
    }: {
      sessionId: number;
      data: SubmitDecisionRequest;
    }) =>
      postJson<DecisionSubmitResult, SubmitDecisionRequest>(
        `/practice-sessions/${sessionId}/decisions`,
        data
      ),
    onError: (err: unknown, { sessionId }) => {
      if (isHttpError(err, 409)) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.practiceSession(sessionId) });
      }
    },
  });
}

export function useCompletePracticeSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionId, data }: { sessionId: number; data: CompleteSessionRequest }) =>
      patchJson<SessionCompleteResult, CompleteSessionRequest>(
        `/practice-sessions/${sessionId}/complete`,
        data
      ),
    onSuccess: (_, { sessionId }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.practiceSession(sessionId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.practiceSessions() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.practiceScore(sessionId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.practicePerformance() });
    },
    onError: (err: unknown, { sessionId }) => {
      if (isHttpError(err, 409)) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.practiceSession(sessionId) });
      }
    },
  });
}

export function useAbandonPracticeSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionId, data }: { sessionId: number; data: AbandonSessionRequest }) =>
      patchJson<SessionAbandonResult, AbandonSessionRequest>(
        `/practice-sessions/${sessionId}/abandon`,
        data
      ),
    onSuccess: (_, { sessionId }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.practiceSession(sessionId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.practiceSessions() });
    },
    onError: (err: unknown, { sessionId }) => {
      if (isHttpError(err, 409)) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.practiceSession(sessionId) });
      }
    },
  });
}

// ============================================================================
// Active sessions (MAX_SESSIONS UI)
// ============================================================================
export const activeSessionsQueryOptions = () =>
  queryOptions({
    queryKey: queryKeys.activeSessions(),
    queryFn: () => getJson<ActiveSession[]>('/auth/sessions'),
    staleTime: 1000 * 30, // 30s — short, list rotates as user activity changes
  });

export const useActiveSessions = () => useQuery(activeSessionsQueryOptions());

export function useRevokeSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: number) =>
      deleteJson<{ success: boolean }>(`/auth/sessions/${sessionId}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.activeSessions() });
    },
  });
}

export function useRevokeAllSessions() {
  return useMutation({
    mutationFn: () => deleteJson<{ success: boolean; count: number }>('/auth/sessions'),
    // No invalidate — caller logs out, all caches get cleared on logout anyway.
  });
}

